import React from "react";
import { Box, Typography, Divider } from "@mui/joy";
import { Hammer, Layers, Hash, Square, Ruler } from "lucide-react";
import { FormInput, FormActionSelect } from "../../../shared";

export function AncillaryAndTrim({ loading }: { loading?: boolean }) {
    return (
        <Box
        >
            <Box mb={2}>
                <Typography level="title-md">Ancillary & Trim</Typography>
                <Typography level="body-sm" color="neutral">
                    Specify details for cornice, fascia, soffit, sheathing, and other trim elements.
                </Typography>
            </Box>

            <Box
                sx={{
                    p: 2,
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.2)",
                    bgcolor: "rgba(128, 128, 128, 0.05)",
                    backdropFilter: "blur(12px)",
                }}
            >
                {/* FIRST ROW: CORNICE, FASCIA, SOFFIT */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(4, 1fr)"
                        },
                        gap: 2.5,
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Cornice Returns (Qty)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Count and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="CorniceReturnsQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 0, minWidth: 0 }}
                                    disabled={loading}
                                    startDecorator={<Hash size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect name="CorniceReturnsAction" disabled={loading} />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Cornice Strip (LF)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Length and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="CorniceStripQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 0, minWidth: 0 }}
                                    disabled={loading}
                                    startDecorator={<Hash size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect name="CorniceStripAction" disabled={loading} />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Fascia Board (LF)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Length and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="FasciaBoardQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 0, minWidth: 0 }}
                                    disabled={loading}
                                    startDecorator={<Hammer size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect name="FasciaBoardAction" disabled={loading} />
                            </Box>
                        </Box>
                    </Box>

                    <FormInput
                        name="SoffitInches"
                        label="Soffit (Inches)"
                        type="number"
                        placeholder="0"
                        helperText="Measurement in inches."
                        disabled={loading}
                        startDecorator={<Ruler size={16} />}
                    />
                </Box>

                <Divider sx={{ my: 2.5, opacity: 0.6 }} />

                {/* SECOND ROW: GUTTER APRON, SHEATHING */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(2, 1fr)"
                        },
                        gap: 2.5,
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Gutter Apron (LF)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Length and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="GutterApronQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 0, minWidth: 0 }}
                                    disabled={loading}
                                    startDecorator={<Layers size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect name="GutterApronAction" disabled={loading} />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Sheathing / OSB (SF)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Area and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="SheathingQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 0, minWidth: 0 }}
                                    disabled={loading}
                                    startDecorator={<Square size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect name="SheathingAction" disabled={loading} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
